import {
  ArrowLeft,
  
  Landmark,
  Palette,
  BookOpen,
  Store,
  UtensilsCrossed,
  Flame,
  Crown,
  Umbrella,
  MapPin,
  Building2,
  Castle,
  Shapes,
  ShoppingBag,
  Armchair,
  Gem,
  Church,
  Eye,
  Droplets,
  History,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareButton } from "@/components/ShareButton";
import { GygActivities } from "@/components/GetYourGuide";

const SITE = "https://cphhighlights.com";

const SPOTS = [
  {
    icon: BookOpen,
    title: "The Black Diamond",
    body: "The Royal Library's black-marble harbour extension. Reading rooms, exhibitions and a café with big windows onto the grey water — somehow better in the rain.",
    meta: "~1h · Free",
    address: "Søren Kierkegaards Plads 1, 1221 København K",
  },
  {
    icon: Castle,
    title: "The National Museum",
    ticket: "https://gyg.me/SXvpX4fb",
    body: "Denmark's biggest museum — Vikings, mummies and Danish everyday life through the ages, inside a grand old prince's palace near Strøget.",
    meta: "~2h · Free permanent collection",
    address: "Ny Vestergade 10, 1471 København K",
  },
  {
    icon: History,
    title: "Københavns Museum",
    body: "The story of Copenhagen itself — from Viking settlement to modern city — in a handsome 18th-century building just behind the City Hall.",
    meta: "~1–1.5h · Paid entry",
    address: "Stormgade 18, 1555 København V",
  },
  {
    icon: ShoppingBag,
    title: "Magasin du Nord",
    body: "Copenhagen's iconic department store on Kongens Nytorv — shop Danish design, fashion and beauty, then hit the gourmet food hall in the basement.",
    meta: "~1h · Free to browse",
    address: "Kongens Nytorv 13, 1095 København K",
  },
  {
    icon: Store,
    title: "Torvehallerne",
    body: "The covered food halls at Nørreport. Smørrebrød, pastries, chocolate and coffee under glass while the rain drums on the roof.",
    meta: "~1h · Free entry — pay for what you eat",
    address: "Frederiksborggade 21, 1362 København K",
  },
  {
    icon: Church,
    title: "Marmorkirken (The Marble Church)",
    body: "The largest church dome in Scandinavia, free to step inside — a calm marble refuge when it pours.",
    meta: "~30–45 min · Free entry",
    address: "Frederiksgade 4, 1265 København K",
  },
  {
    icon: Eye,
    title: "Christiansborg Tower",
    body: "Ride the lift to the top of the city's tallest tower — a free rooftop view over the wet spires and rooftops.",
    meta: "~45 min · Free",
    address: "Christiansborg Slotsplads, 1218 København K",
  },
  {
    icon: Landmark,
    title: "The Glyptoteket",
    body: "Sculptures from antiquity to Rodin around a glass-roofed winter garden full of palm trees. The prettiest room in Copenhagen to be rained into.",
    meta: "~2h · Paid entry",
    address: "Dantes Plads 7, 1556 København V",
  },
  {
    icon: Palette,
    title: "SMK — the national gallery",
    body: "Danish and international art in the national gallery — deep enough to lose a wet afternoon in.",
    meta: "~1.5–2h · Paid entry",
    address: "Sølvgade 48–50, København K",
  },
  {
    icon: Armchair,
    title: "Designmuseum Danmark",
    body: "Danish design from chairs to posters — and the café does excellent open sandwiches.",
    meta: "~1.5–2h · Paid entry",
    address: "Bredgade 68, København K",
  },
  {
    icon: Shapes,
    title: "Thorvaldsens Museum",
    body: "Bertel Thorvaldsen's monumental sculptures in Copenhagen's oldest museum building — colourful, calm and tucked right behind Christiansborg.",
    meta: "~1–1.5h · Paid entry",
    address: "Bertel Thorvaldsens Plads 2, 1213 København K",
  },
  {
    icon: Building2,
    title: "DAC — Danish Architecture Center",
    ticket: "https://gyg.me/ms7UuAEM",
    body: "The architecture centre in the BLOX building on the harbour. Exhibitions on how cities get made, a harbour-view café and the best design bookshop in town.",
    meta: "~1–1.5h · Paid entry",
    address: "Bryghuspladsen 10, 1473 København K",
  },
  {
    icon: Droplets,
    title: "Cisternerne",
    body: "The old underground water reservoirs beneath Søndermarken — dripstones, total darkness and art installations with a 17-second echo. Rain literally cannot reach you down here.",
    meta: "~1h · Paid entry",
    address: "Søndermarken, Roskildevej 25A, 2000 Frederiksberg",
  },
  {
    icon: Gem,
    title: "Rosenborg Castle",
    ticket: "https://gyg.me/Sg1VJNVD",
    body: "The crown jewels sit in the cellar vaults of Christian IV's Renaissance castle — proper indoor sightseeing.",
    meta: "~1–1.5h · Paid entry",
    address: "Øster Voldgade 4A, København K",
  },
  {
    icon: Crown,
    title: "Amalienborg",
    body: "The palace museum walks you through the royal family's actual rooms, right on the octagonal square.",
    meta: "~1–1.5h · Paid entry",
    address: "Amalienborg Slotsplads, København K",
  },
  {
    icon: Flame,
    title: "CopenHot",
    body: "Hot tubs and saunas by the harbour. Sitting in 39-degree water while rain hits the harbour around you is a very Copenhagen kind of luxury.",
    meta: "~1.5h · Book ahead",
    address: "Refshalevej 325, 1432 København K",
  },
  {
    icon: UtensilsCrossed,
    title: "A long lunch",
    body: "Rain is the perfect excuse. Pick a food hall or a cosy bistro, order the smørrebrød, and let the afternoon happen.",
    meta: "As long as you like",
    link: { label: "The smørrebrød guide", to: "/smorrebrod" },
  },
];

const RAIN_FAQ = [
  {
    q: "Does it rain a lot in Copenhagen?",
    a: "It rains on roughly 170 days a year, but rarely all day — Copenhagen rain is usually showers that pass. Pack a light rain jacket whatever the month and you'll be fine.",
  },
  {
    q: "What's the best free indoor thing to do?",
    a: "The Black Diamond library is free and impressive, Torvehallerne food market too. Browsing Magasin du Nord or Illum (high-end department store) costs nothing either.",
  },
  {
    q: "Is Tivoli worth it in the rain?",
    a: "Tivoli does lose some magic, but the rides keep running and the food hall is free to enter without entering Tivoli. With a rain jacket it can still be a good idea.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: RAIN_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "What to Do in Copenhagen on a Rainy Day",
  url: `${SITE}/rainy-day`,
  description:
    "What to do in Copenhagen when it rains — museums, food halls, board-game cafés, saunas and the best covered spots, from locals.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Rainy day in Copenhagen", item: `${SITE}/rainy-day` },
  ],
};

export default function RainyDayPage() {
  return (
    <main data-testid="rainy-day-page" className="bg-[#F9F8F6] text-black font-body">
      <Seo
        title="What to Do in Copenhagen on a Rainy Day"
        description="What to do in Copenhagen when it rains — museums, food halls, board-game cafés, harbour saunas and cosy churches. A local's rainy-day guide to Copenhagen."
        path="/rainy-day"
        image="https://cphhighlights.com/planner/229f24a483bf.webp"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-zinc-200 pt-16 md:pt-20">
        <div className="absolute inset-0">
          <img src={"https://cphhighlights.com/planner/229f24a483bf.webp"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-12 md:mb-14">
              <Link
                to="/"
                data-testid="rainy-day-back"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-[#DF3226] text-white px-5 py-2.5 rounded-full border border-black shadow-brut hover:-translate-y-0.5 transition-all"
              >
                <ArrowLeft size={16} /> Back to start
              </Link>
              <ShareButton title="Copenhagen in the Rain — Indoor Guide" testid="rainy-share" />
            </div>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              IT RAINS ~170 DAYS A YEAR
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
              Copenhagen<br />
              <span className="font-editorial italic font-600">on a rainy day</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mt-6 leading-relaxed">
              Locals don't cancel plans for weather — they just move inside. Here's where the city
              gets better when it rains: palm-filled winter gardens, food halls, grand museums and
              harbour hot tubs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* indoor spots */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPOTS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05} y={30}>
                <div
                  data-testid={`rainy-spot-${i}`}
                  className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <s.icon size={22} className="text-[#DF3226]" />
                  <h2 className="font-display font-900 tracking-tight text-xl mt-3 leading-tight">{s.title}</h2>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{s.body}</p>
                  <p className="font-body font-700 text-[11px] tracking-widest text-[#E8590C] mt-4">{s.meta.toUpperCase()}</p>
                  {s.address && (
                    <p className="font-body text-xs text-black/50 mt-2 flex items-center gap-1.5">
                      <MapPin size={12} className="shrink-0 text-black/40" /> {s.address}
                    </p>
                  )}
                  {s.link && (
                    <Link
                      to={s.link.to}
                      data-testid={`rainy-spot-link-${i}`}
                      className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      {s.link.label}
                    </Link>
                  )}
                  {s.ticket && (
                    <a
                      href={s.ticket}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`rainy-spot-ticket-${i}`}
                      className="inline-block mt-3 ml-4 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      Tickets
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* planner CTA */}
      <section data-testid="rainy-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24 text-center">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">DRY DAYS TOO</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl mt-4 max-w-2xl mx-auto">
              Build the rain plan into <span className="font-editorial italic font-600">your trip</span>
            </h2>
            <p className="font-body text-base text-white/80 max-w-xl mx-auto mt-5">
              Keep a wet-weather day up your sleeve — open the planner and mix indoor stops with
              everything else you want to see.
            </p>
            <Link
              to="/#planner"
              data-scroll="#planner"
              data-testid="rainy-cta-planner"
              className="mt-8 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
            >
              Open the planner
            </Link>
            <p className="font-body text-sm text-white/60 mt-5">
              Sunshine back?{" "}
              <Link to="/things-to-do" className="underline underline-offset-2 hover:text-white transition-colors">
                Browse things to do
              </Link>{" "}
              for the dry-day list.
            </p>
          </Reveal>
        </div>
      </section>

      {/* bookable indoor tours — museums & castles search widget */}
      <section data-testid="rainy-tours" className="py-12 md:py-16 bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-6">MUSEUMS & CASTLES — BOOK AHEAD</p>
          <GygActivities q="København museum castle" numberOfItems={3} />
        </div>
      </section>

      <ExploreLinks
        testid="explore-rainy"
        links={[
          { label: "Sun's back? Things to do in Copenhagen", to: "/things-to-do" },
          { label: "The ready-made 3 days — Copenhagen itinerary", to: "/copenhagen-itinerary" },
          { label: "Kids bouncing off walls? The family guide", to: "/copenhagen-with-kids" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section className="pb-12 md:pb-20 border-t border-dotted border-zinc-300 pt-12 md:pt-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-6">
            Rain questions, answered
          </h2>
          <div className="space-y-3">
            {RAIN_FAQ.map((f, i) => (
              <details
                key={f.q}
                data-testid={`rainy-faq-${i}`}
                className="group bg-white border border-zinc-200 rounded-xl px-5 py-4"
              >
                <summary className="cursor-pointer list-none font-display font-800 text-base md:text-lg flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[#DF3226] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Copenhagen in the Rain — Indoor Guide" />
    </main>
  );
}
